import { ArrowBack } from '@mui/icons-material';
import { Box, Button, Stack, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { getTestCasesBySuite } from '../../../../modules/testsuites';
import TableComponent from '../../../../ui/data/table/TableComponent';
import { testCasestableHeader } from '../../test-case/testCaseTableHead';
import MultiSelectDropdown from '../../../../ui/primitives/MultiSelectDropdown';
import CustomSelect from '../../../../ui/primitives/CustomSelect';

const ALL_OPTION = { id: 'All', name: 'All' };

const SelectTestCasesFromSuiteModal = ({
  suite,
  selectedTestCases,
  setSelectedTestCases,
  handleBack,
  testCycleId,
  technologyLayerOptions = [],
}) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [order, setOrder] = useState('desc');
  const [filters, setFilters] = useState({
    module: 'All',
    technologyLayer: 'All',
    labels: [],
  });
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [filteredModuleOptions, setFilteredModuleOptions] = useState([]);
  const [filteredLabelOptions, setFilteredLabelOptions] = useState([]);

  useEffect(() => {
    const fetchTestCases = async () => {
      setLoading(true);
      try {
        const response = await getTestCasesBySuite(
          suite.id,
          page,
          perPage,
          true,
          testCycleId,
          filters
        );

        const formattedTestCases = response.testcases.map((testcase) => ({
          ...testcase,
          labelNames: testcase?.label_details?.map((l) => l.name),
        }));

        setData(formattedTestCases);
        setTotalCount(response.total);
        setFilteredModuleOptions(response.available_modules || []);
        setFilteredLabelOptions(response.available_labels || []);
      } catch (error) {
        console.error('Failed to fetch test cases', error);
      } finally {
        setLoading(false);
      }
    };

    fetchTestCases();
  }, [suite, testCycleId, page, perPage, filters]);

  const handleSelectFilter = (name) => (value) => {
    setFilters((prev) => ({ ...prev, [name]: value }));
    setPage(1);
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };
  const handlePageSizeChange = (e) => {
    const newPerPage = parseInt(e.target.value, 10);
    setPerPage(newPerPage);
    setPage(1);
  };

  return (
    <Box sx={{ minHeight: '85vh', maxHeight: '85vh', mt: 1 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="subtitle1">
          Test Suite: <strong>{suite.name}</strong>
        </Typography>

        <Button
          title="Back to Test Suites"
          onClick={handleBack}
          variant="outlined"
          startIcon={<ArrowBack />}>
          Back to Test Suites
        </Button>
      </Box>

      <Stack direction="row" spacing={2} mb={2} flexWrap="wrap">
        <Box sx={{ width: 300 }}>
          <CustomSelect
            id="technologyLayer"
            label="Technology Layer"
            value={filters.technologyLayer}
            onChange={handleSelectFilter('technologyLayer')}
            options={[ALL_OPTION, ...technologyLayerOptions.map(({ id, name }) => ({ id: name, name }))]}
          />
        </Box>

        <Box sx={{ width: 300 }}>
          <CustomSelect
            id="module"
            label="Module"
            value={filters.module}
            onChange={handleSelectFilter('module')}
            options={[ALL_OPTION, ...filteredModuleOptions]}
          />
        </Box>

        <Box sx={{ width: 300 }}>
          <MultiSelectDropdown
            options={filteredLabelOptions}
            selectedValues={filters.labels}
            onChange={(selected) => {
              setFilters((prev) => ({ ...prev, labels: selected }));
              setPage(1);
            }}
            maxSelections={filteredLabelOptions.length}
            placeholder="Select Labels"
            name="labels"
            label="Labels"
            sx={{ width: '100%' }}
          />
        </Box>
      </Stack>

      <TableComponent
        data={data}
        headerTabs={testCasestableHeader}
        order={order}
        setOrder={setOrder}
        defaultSort="created_at"
        isLoading={loading}
        striped
        showCheckbox
        selected={selectedTestCases}
        setSelected={setSelectedTestCases}
        isRowDisabled={(row) => row.is_linked_to_cycle}
        tablekey="test cases"
        page={page}
        perPage={perPage}
        totalCount={totalCount}
        showPages={true}
        showCount={true}
        handlePageChange={handlePageChange}
        setPerPage={handlePageSizeChange}
        showRowCount={true}
      />
    </Box>
  );
};

export default SelectTestCasesFromSuiteModal;
